import React, { useEffect, useRef, useState } from "react";
import {
    completeMovmoOAuth,
    StateMismatchError,
    MissingVerifierError,
} from "@movmo/connect-button";
import {
    CONNECT_BUTTON_CLIENT_ID,
    CONNECT_BUTTON_REDIRECT_URI,
    CONNECT_BUTTON_TOKEN_URL,
} from "./config";

interface CallbackResult {
    ok: boolean;
    message: string;
    detail?: unknown;
}

interface TokenStage {
    state: "idle" | "pending" | "success" | "error";
    message?: string;
    detail?: unknown;
}

const CARD: React.CSSProperties = {
    maxWidth: 720,
    margin: "60px auto",
    background: "#fff",
    border: "1px solid #e7e5e4",
    borderRadius: 12,
    padding: 32,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const PRE: React.CSSProperties = {
    background: "#fafaf9",
    border: "1px solid #e7e5e4",
    borderRadius: 6,
    padding: 12,
    fontSize: 12,
    // The connect-button package's bundled Tailwind preflight resets inherited
    // text color, so the JSON renders white on near-white without an explicit
    // color set here.
    color: "#171412",
    fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    overflowX: "auto",
    wordBreak: "break-all",
    whiteSpace: "pre-wrap",
};

/**
 * Two independent verdicts:
 *  (1) Package layer — completeMovmoOAuth validates state (CSRF) and returns
 *      { code, codeVerifier }. This is what MOVMO-348 actually owns.
 *  (2) Token exchange — POSTs to the proxied token endpoint. Lives here only
 *      because we have no partner backend in this test harness; real partners
 *      run this server-side with client_secret.
 */
export default function ConnectButtonCallback() {
    const [result, setResult] = useState<CallbackResult | null>(null);
    const [tokenStage, setTokenStage] = useState<TokenStage>({ state: "idle" });
    // React 18 StrictMode runs effects twice in dev. Two unguarded passes both
    // read the verifier from sessionStorage before either clears it (microtask
    // interleave), then both POST the same auth code to /v1/oauth/token —
    // the second always fails with "authorization code has already been
    // redeemed" because OAuth codes are single-use. Guard with a ref so only
    // the first invocation actually runs the exchange. Same pattern used in
    // AeroCheckout to prevent double Darwin-hold calls.
    const didRun = useRef(false);

    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;

        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        const errParam = params.get("error");

        if (errParam) {
            setResult({
                ok: false,
                message: `Authorization server returned error: ${errParam}`,
                detail: { error_description: params.get("error_description") },
            });
            return;
        }
        if (!code || !state) {
            setResult({
                ok: false,
                message: "Missing code or state in callback URL.",
                detail: { search: window.location.search },
            });
            return;
        }

        completeMovmoOAuth(code, state)
            .then((completion) => {
                // (1) Package verdict
                setResult({
                    ok: true,
                    message:
                        "completeMovmoOAuth() succeeded — state validated, verifier returned.",
                    detail: {
                        code: `${completion.code.slice(0, 12)}…(${
                            completion.code.length
                        } chars)`,
                        codeVerifier: `${completion.codeVerifier.slice(
                            0,
                            12,
                        )}…(${completion.codeVerifier.length} chars)`,
                    },
                });

                // (2) Token exchange — public PKCE client, no client_secret.
                setTokenStage({ state: "pending" });
                const body = new URLSearchParams({
                    grant_type: "authorization_code",
                    client_id: CONNECT_BUTTON_CLIENT_ID,
                    code: completion.code,
                    code_verifier: completion.codeVerifier,
                    redirect_uri: CONNECT_BUTTON_REDIRECT_URI,
                });
                return fetch(CONNECT_BUTTON_TOKEN_URL, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body: body.toString(),
                });
            })
            .then(async (res) => {
                if (!res) return;
                const json = await res.json();
                if (!res.ok) {
                    setTokenStage({
                        state: "error",
                        message: `Token endpoint returned HTTP ${res.status}`,
                        detail: json,
                    });
                    return;
                }
                const trimmed: Record<string, unknown> = {};
                for (const [k, v] of Object.entries(json)) {
                    trimmed[k] =
                        typeof v === "string" && v.length > 60
                            ? `${v.slice(0, 40)}…(${v.length} chars)`
                            : v;
                }
                setTokenStage({ state: "success", detail: trimmed });
            })
            .catch((err) => {
                if (err instanceof StateMismatchError) {
                    setResult({
                        ok: false,
                        message: "StateMismatchError — CSRF check failed.",
                        detail: String(err),
                    });
                } else if (err instanceof MissingVerifierError) {
                    setResult({
                        ok: false,
                        message:
                            "MissingVerifierError — sessionStorage was cleared (or you reloaded after auth).",
                        detail: String(err),
                    });
                } else {
                    // Distinguish: did the package throw, or did the fetch?
                    if (result === null) {
                        setResult({
                            ok: false,
                            message: `Unexpected error before package verdict: ${
                                (err as Error).message
                            }`,
                            detail: String(err),
                        });
                    } else {
                        setTokenStage({
                            state: "error",
                            message: `Token exchange request failed: ${
                                (err as Error).message
                            }`,
                            detail: { error: String(err) },
                        });
                    }
                }
            });
    }, []);

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "#f5f5f4",
                fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
                padding: 40,
            }}
        >
            <div style={CARD}>
                <h1 style={{ fontSize: 20, margin: 0, color: "#1B2A4A" }}>
                    OAuth callback
                </h1>
                <p
                    style={{
                        color: "#57534E",
                        fontSize: 13,
                        marginTop: 8,
                        marginBottom: 24,
                    }}
                >
                    Two independent verdicts: <strong>(1)</strong> the package's{" "}
                    <code>completeMovmoOAuth()</code> result, and{" "}
                    <strong>(2)</strong> the live token exchange against{" "}
                    <code>{CONNECT_BUTTON_TOKEN_URL}</code> (proxied through
                    CRA's setupProxy.js to bypass browser CORS).
                </p>

                <h2
                    style={{
                        fontSize: 16,
                        marginTop: 24,
                        color: "#1B2A4A",
                    }}
                >
                    (1) Package verdict
                </h2>
                {result === null ? (
                    <p style={{ color: "#57534E", fontSize: 13 }}>
                        Running completeMovmoOAuth()…
                    </p>
                ) : (
                    <>
                        <p
                            style={{
                                color: result.ok ? "#15803D" : "#B91C1C",
                                fontWeight: 600,
                            }}
                        >
                            {result.message}
                        </p>
                        {result.detail !== undefined && (
                            <pre style={PRE}>
                                {JSON.stringify(result.detail, null, 2)}
                            </pre>
                        )}
                    </>
                )}

                <h2
                    style={{
                        fontSize: 16,
                        marginTop: 32,
                        color: "#1B2A4A",
                    }}
                >
                    (2) Token exchange
                </h2>
                {tokenStage.state === "idle" && (
                    <p style={{ color: "#57534E", fontSize: 13 }}>
                        Waiting on (1)…
                    </p>
                )}
                {tokenStage.state === "pending" && (
                    <p style={{ color: "#57534E", fontSize: 13 }}>
                        POSTing to token endpoint…
                    </p>
                )}
                {tokenStage.state === "success" && (
                    <>
                        <p style={{ color: "#15803D", fontWeight: 600 }}>
                            Token exchange succeeded.
                        </p>
                        <pre style={PRE}>
                            {JSON.stringify(tokenStage.detail, null, 2)}
                        </pre>
                    </>
                )}
                {tokenStage.state === "error" && (
                    <>
                        <p style={{ color: "#B91C1C", fontWeight: 600 }}>
                            {tokenStage.message}
                        </p>
                        {tokenStage.detail !== undefined && (
                            <pre style={PRE}>
                                {JSON.stringify(tokenStage.detail, null, 2)}
                            </pre>
                        )}
                    </>
                )}

                <p
                    style={{
                        color: "#57534E",
                        fontSize: 13,
                        marginTop: 24,
                    }}
                >
                    <a href="/connect-button">← back to start</a>
                </p>
            </div>
        </div>
    );
}
